import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { api, setUser, getUser } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Truck, ArrowRight, ShieldCheck } from "lucide-react";
import { capture } from "../lib/analytics";

/**
 * RoleSelection — the mandatory one-time screen shown to any user whose
 * `needs_role_selection` flag is true (Apple/Google OAuth signups). Two
 * mutually-exclusive tiles: "I want to Clear Waste" (homeowner) vs "I want
 * to Carry Waste" (carrier). Carrier requires ticking the independent-
 * contractor consent checkbox.
 */
export default function RoleSelection() {
  const navigate = useNavigate();
  const initial = getUser();
  const [user, setLocalUser] = useState(initial);
  const [choice, setChoice] = useState(null); // 'homeowner' | 'carrier' | null
  const [acceptedCarrier, setAcceptedCarrier] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    // If someone bypasses the flow and lands here without needing to pick,
    // send them straight to their dashboard.
    if (!user.needs_role_selection) {
      navigate(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const submit = async () => {
    if (!choice) {
      toast.error("Pick one to continue");
      return;
    }
    if (choice === "carrier" && !acceptedCarrier) {
      toast.error("You need to accept the independent-contractor terms first");
      return;
    }
    setSaving(true);
    try {
      const { data: updated } = await api.post(
        `/auth/finalize-role`,
        { role: choice, accepted_carrier_terms: acceptedCarrier },
        { params: { user_id: user.id } }
      );
      setUser(updated);
      setLocalUser(updated);
      capture("role_selected", { role: choice });
      toast.success(choice === "carrier" ? "Welcome, carrier — let's finish onboarding." : "You're all set — post your first job!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your choice.");
    } finally {
      setSaving(false);
    }
  };

  if (!user || !user.needs_role_selection) return null;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="inline-flex items-center gap-2 bg-[#EBE8E0] text-[#1B3624] px-3 py-1.5 rounded-full text-[10px] font-semibold tracking-wide uppercase">
          <ShieldCheck className="w-3 h-3" /> One last step
        </div>
        <h1
          className="mt-4 text-3xl sm:text-4xl tracking-tighter font-medium text-[#1B3624]"
          style={{ fontFamily: "Outfit" }}
          data-testid="role-selection-title"
        >
          Which side of Binshare are you?
        </h1>
        <p className="mt-2 text-sm sm:text-base text-[#6B6965]">
          Pick the option that fits — this decides your primary dashboard.
        </p>

        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <RoleTile
            testId="role-homeowner"
            active={choice === "homeowner"}
            onClick={() => setChoice("homeowner")}
            icon={Trash2}
            iconTint="#4A6B56"
            eyebrow="I want to"
            title="Clear waste"
            body="Post a photo, get bids from licensed UK carriers, pay in-app. Junk removal, house clearance, garden waste — sorted."
          />
          <RoleTile
            testId="role-carrier"
            active={choice === "carrier"}
            onClick={() => setChoice("carrier")}
            icon={Truck}
            iconTint="#D67B54"
            eyebrow="I want to"
            title="Carry waste"
            body="Licensed waste-carrier account. Browse local leads, submit bids, get paid via Stripe. Binshare keeps 5% — you keep 95%."
          />
        </div>

        {choice === "carrier" && (
          <Card data-testid="carrier-terms" className="mt-4 p-5 rounded-2xl border-[#D67B54]/40 bg-[#FDF2EB]">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                data-testid="carrier-terms-checkbox"
                checked={acceptedCarrier}
                onCheckedChange={(v) => setAcceptedCarrier(Boolean(v))}
                className="mt-0.5"
              />
              <span className="text-xs text-[#7A4A2A] leading-relaxed">
                I confirm I hold (or will hold before bidding) a valid <strong>UK Waste Carrier Licence</strong>, that I operate as an <strong>independent contractor</strong> — not a Binshare employee — and that I'm responsible for my own waste-transfer notes, insurance, tax and disposal at licensed facilities.
              </span>
            </label>
          </Card>
        )}

        <Button
          data-testid="role-selection-submit"
          onClick={submit}
          disabled={!choice || saving || (choice === "carrier" && !acceptedCarrier)}
          className="w-full sm:w-auto mt-6 rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white py-6 px-8"
        >
          {saving ? "Saving…" : "Finish setup"} <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </Layout>
  );
}

function RoleTile({ testId, active, onClick, icon: Icon, iconTint, eyebrow, title, body }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`text-left rounded-3xl p-6 border-2 transition ${
        active
          ? "border-[#1B3624] bg-[#F6F4F0] shadow-sm"
          : "border-[#E2DFD8] bg-white hover:border-[#1B3624]/40 hover:bg-[#FBFAF6]"
      }`}
    >
      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center`} style={{ background: iconTint }}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="mt-4 text-[10px] uppercase tracking-wider text-[#9B9893] font-semibold">{eyebrow}</div>
      <h3 className="mt-1 text-2xl font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }}>
        {title}
      </h3>
      <p className="mt-2 text-sm text-[#6B6965] leading-relaxed">{body}</p>
    </button>
  );
}
